/**
 * The hidden A/B picker: five quick taps on the avatar at the reveal open it,
 * and a tester pins this device to one arm of the cartoon-gate experiment —
 * A = teaser (control), B = full — or hands it back to Mixpanel. The choice
 * lives in localStorage, so it survives the rest of the flow and the next
 * visit, and every event from a pinned device says `gate_forced: true`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { forcedGate, setForcedGate, type CartoonGate } from './gate';

/** `n` taps within a couple of seconds, like the paywall's test-mode taps. */
export function useSecretTaps(n: number, windowMs = 2500) {
  const taps = useRef<number[]>([]);
  const [open, setOpen] = useState(false);
  const tap = useCallback(() => {
    const now = Date.now();
    taps.current = [...taps.current.filter((t) => now - t < windowMs), now];
    if (taps.current.length >= n) { taps.current = []; setOpen(true); }
  }, [n, windowMs]);
  const close = useCallback(() => setOpen(false), []);
  return { tap, open, close };
}

export function GatePicker({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState<CartoonGate | null>(() => forcedGate());
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const pick = (g: CartoonGate | null) => { setForcedGate(g); setCurrent(g); };
  return (
    <div className="gatepick" role="dialog" aria-label="Cartoon A/B test" onClick={(e) => e.stopPropagation()}>
      <strong>Cartoon A/B (this device)</strong>
      <div className="gatepick-row">
        <button className={current === 'teaser' ? 'on' : ''} onClick={() => pick('teaser')}>A · Teaser<small>one scene, then unlock</small></button>
        <button className={current === 'full' ? 'on' : ''} onClick={() => pick('full')}>B · Full<small>whole cartoon</small></button>
        <button className={current === null ? 'on' : ''} onClick={() => pick(null)}>Auto<small>Mixpanel decides</small></button>
      </div>
      <button className="gatepick-close" onClick={onClose}>Done</button>
    </div>
  );
}
